// Orchestration wrapper over the pure @physics/grid loader. The pure loader
// runs the HDF5 fetch + parse and reports progress via a callback; we relay
// that as the 'ui:loading:progress' intent, and bracket each load with
// show/hide of the loading overlay. Belongs in @app/ (not @physics/) because
// it dispatches store intents.

import {
  fetchFieldComponents as fetchCompPure,
  fetchFieldMetadata as fetchMetaPure,
} from '@physics/grid';
import type { FieldComponentsResult, FieldMetadata } from '@physics/types';
import { store } from '@store/instance';
import { INTENTS } from '@store/intents';
import { debugWarn } from '@adapters/debug';

// Viewer shape covers both real ViewerContext (from app/context) and the
// looser legacy shape; index signature lets us tolerate either.
interface ViewerLike {
  sceneMeta: { coordinateSystem: string; units: string };
  grid: {
    xMin: number; xMax: number;
    yMin: number; yMax: number;
    zMin: number; zMax: number;
    resolutionX: number; resolutionY: number; resolutionZ: number;
    nx: number; ny: number; nz: number;
  };
  field: {
    metadata: FieldMetadata | Partial<FieldMetadata> | null;
    data: Record<string, Float32Array | undefined> | null;
    gridFlat: Float32Array | null;
    currentComponent: string;
    modelName: string;
    dataTime: number | string | null;
    dataTimeUnits: string | null;
  };
}

interface ParamsLike {
  fieldComponent?: string;
}

const onProgress = (value: number): void => store.dispatch(INTENTS.UI_LOADING_PROGRESS, { value });

export async function fetchFieldMetadata(hdf5Url: string): Promise<FieldMetadata | null> {
  store.dispatch(INTENTS.UI_LOADING_SHOW, { msg: 'Loading dataset…' });
  try {
    return await fetchMetaPure(hdf5Url, { onProgress });
  } finally {
    store.dispatch(INTENTS.UI_LOADING_HIDE);
  }
}

export async function fetchFieldComponents(hdf5Url: string, fieldComponents: string[]): Promise<FieldComponentsResult | null> {
  store.dispatch(INTENTS.UI_LOADING_SHOW, { msg: 'Loading dataset…' });
  try {
    return await fetchCompPure(hdf5Url, fieldComponents, { onProgress });
  } finally {
    store.dispatch(INTENTS.UI_LOADING_HIDE);
  }
}

/**
 * Fetch HDF5 metadata and write it into viewer.{grid,sceneMeta,field}.
 * Pure I/O + viewer writes; no UI side effects beyond the loading overlay
 * inherited from fetchFieldMetadata. Returns true on success, false on failure.
 */
export async function loadFieldMetadata(viewer: ViewerLike, hdf5Url: string): Promise<boolean> {
  try {
    const meta = await fetchFieldMetadata(hdf5Url);
    if (!meta) return false;
    viewer.sceneMeta.coordinateSystem = meta.coordinate_system;
    viewer.sceneMeta.units = meta.units;
    viewer.grid.xMin = meta.grid_bounds.x.min;
    viewer.grid.xMax = meta.grid_bounds.x.max;
    viewer.grid.yMin = meta.grid_bounds.y.min;
    viewer.grid.yMax = meta.grid_bounds.y.max;
    viewer.grid.zMin = meta.grid_bounds.z.min;
    viewer.grid.zMax = meta.grid_bounds.z.max;
    viewer.grid.resolutionX = meta.resolution.x;
    viewer.grid.resolutionY = meta.resolution.y;
    viewer.grid.resolutionZ = meta.resolution.z;
    viewer.grid.nx = meta.dims.nx;
    viewer.grid.ny = meta.dims.ny;
    viewer.grid.nz = meta.dims.nz;
    viewer.field.metadata = {
      coordinate_system: meta.coordinate_system,
      units: meta.units,
      grid_bounds: meta.grid_bounds,
      resolution: meta.resolution,
      field_components: meta.field_components,
      field_units: meta.field_units,
      field_name_short: meta.field_name_short ?? null,
      field_name_long: meta.field_name_long ?? null,
      title: meta.title,
      model_name: meta.model_name,
      time: meta.time,
      time_units: meta.time_units,
      doi: meta.doi,
      summary: meta.summary,
    };
    viewer.field.modelName = meta.model_name || meta.title || '';
    viewer.field.dataTime = meta.time ?? null;
    viewer.field.dataTimeUnits = meta.time_units ?? '';
    return true;
  } catch (err) {
    debugWarn(viewer as unknown as { sceneMeta?: { debugEnabled?: boolean } }, 'Error loading field metadata from HDF5:', err);
    return false;
  }
}

/**
 * Fetch HDF5 field components and populate viewer.field.{data, gridFlat,
 * currentComponent}.
 */
export async function loadFieldComponents(viewer: ViewerLike, hdf5Url: string, params: ParamsLike): Promise<boolean> {
  try {
    const meta = viewer.field.metadata;
    const fieldComponents = (meta && 'field_components' in meta && Array.isArray(meta.field_components))
      ? (meta.field_components as string[])
      : [];
    if (fieldComponents.length === 0) return false;
    const result = await fetchFieldComponents(hdf5Url, fieldComponents);
    if (!result) return false;
    const { componentsData, magnitude } = result;
    if (!viewer.field.data) viewer.field.data = {};
    for (const name of fieldComponents) {
      viewer.field.data[name] = componentsData[name];
    }
    viewer.field.gridFlat = magnitude;
    try {
      if (params && typeof params.fieldComponent === 'string' && fieldComponents.includes(params.fieldComponent)) {
        viewer.field.currentComponent = params.fieldComponent;
      } else if (fieldComponents.includes('Bx') && fieldComponents.includes('By') && fieldComponents.includes('Bz')) {
        viewer.field.currentComponent = 'magnitude';
      } else if (fieldComponents.length > 0) {
        const first = fieldComponents[0]!;
        viewer.field.currentComponent = first;
        store.dispatch(INTENTS.SET_FIELD_COMPONENT, { component: first });
      } else {
        viewer.field.currentComponent = 'magnitude';
      }
    } catch {
      /* swallow */
    }
    return true;
  } catch (err) {
    debugWarn(viewer as unknown as { sceneMeta?: { debugEnabled?: boolean } }, 'Error loading HDF5 field:', err);
    return false;
  }
}
